import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Col,
  Row,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
  UncontrolledDropdown,
  Alert
} from 'reactstrap';
import ReactLoading from 'react-loading';
import { get } from 'lodash-es';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import { useAxios } from '../../../hooks';
import { selectHouseRef } from '../../../selectors';
import CatalogGroupCheckbox from './CatalogGroupCheckbox';
import { invoicesPath, imagePath } from '../../../utilities/UrlPaths';
import CreatingInvoiceModal from './InvoiceCreationInprogressModal';

const TEMPLATES_LABEL_PLACEHOLDER = 'Choose Invoice Template';

const blockEl = 'invoices__uninvoiced-sessions';

const UninvoicedSessions = ({ toggleCatalogsNotInSameLocationBanner }) => {
  const [currentGroupId, setCurrentGroupId] = useState('');
  const [invoiceSettingsID, setInvoiceSettingsID] = useState(0);
  const [templateslabel, setTemplatesLabel] = useState(TEMPLATES_LABEL_PLACEHOLDER);
  const [groupTitle, setGroupTitle] = useState('');
  const [catalogRefs, setCatalogRefs] = useState([]);
  const groupsPerPage = 25;
  const [startRowIndex, setStartRowIndex] = useState(0);
  const [endRowIndex, setEndRowIndex] = useState(startRowIndex + groupsPerPage);
  const [currentPage, setCurrentPage] = useState(0);
  const [areCatalogsNotInSameLocation, setAreCatalogsNotInSameLocation] = useState(false);
  const { paymentProcessingEnabled } = useSelector(state => state.house);
  const [payrixTemplate, setPayrixTemplate] = useState();
  const [groupNameLength, setGroupNameLength] = useState(0);
  const [isCreateInvoiceDisabled, setIsCreateInvoiceDisabled] = useState(true);
  const [isOpenCreatingInvoiceModal, setIsOpenCreatingInvoiceModal] = useState(false);
  const [isShowInvoiceCreationError, setIsShowInvoiceCreationError] = useState(false);
  const houseRef = useSelector(selectHouseRef);
  const { data: invoiceCatalogsData, isIdle, isLoading } = useAxios(() => `/app/house/${houseRef}/invoiceCatalogs`, [houseRef]);
  const { data: templatesData } = useAxios(() => `/app/house/${houseRef}/invoiceSettings`, [houseRef]);
  const { exec, isError, data: invoiceCreatedData } = useAxios(
    () => `/app/house/${houseRef}/invoiceGroup`,
    [houseRef],
    { method: 'post' },
    { manual: true }
  );

  const { data: paymentProcessorTypeData } = useAxios(
    () => `/app/house/${houseRef}/paymentProcessorType`,
    [houseRef]
  );

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const queryPage = queryParams.get('page') || 0;
    setCurrentPage(queryPage);
  }, []);

  useEffect(() => {
    setStartRowIndex(currentPage * groupsPerPage);
    setEndRowIndex((currentPage * groupsPerPage) + groupsPerPage);
  }, [currentPage]);

  useEffect(() => {
    if (invoiceCreatedData) {
      // re-enable button when post completes / data is returned
      setIsCreateInvoiceDisabled(false);
      setIsOpenCreatingInvoiceModal(false);

      const invoiceGroupID = get(invoiceCreatedData, 'data.data.invoiceGroupID', '');

      if (invoiceGroupID) {
        window.location.href = `${invoicesPath}/CreateNewInvoices/${invoiceGroupID}`;
      }
    } else if (isError) {
      setIsCreateInvoiceDisabled(false);
      setIsOpenCreatingInvoiceModal(false);
      setIsShowInvoiceCreationError(true);
    }
  }, [invoiceCreatedData, isError]);

  const processorType = useMemo(() => get(paymentProcessorTypeData, 'data.data.processor-type'), [paymentProcessorTypeData]);

  const invoiceTemplates = useMemo(() => {
    const templates = get(templatesData, 'data.data.invoiceSettings', []);
    if (processorType === 'p' && paymentProcessingEnabled) {
      const filterTemplates = templates.filter(item => item.payrixTemplate === true);
      return filterTemplates;
    }
    return templates;
  }, [templatesData, processorType]);

  const isTemplatesLabelPlaceholderVisible = useMemo(() => templateslabel === TEMPLATES_LABEL_PLACEHOLDER, [templateslabel]);

  const auctionGroups = useMemo(() => get(invoiceCatalogsData, 'data.data.auctionGroups', []), [invoiceCatalogsData]);

  const totalGroups = useMemo(() => auctionGroups.length, [auctionGroups]);

  const invoiceGroups = useMemo(() => auctionGroups.filter((_, index) => index >= startRowIndex && index < endRowIndex), [auctionGroups]);

  useEffect(() => {
    const isDisabledButton = !(invoiceSettingsID && groupTitle && catalogRefs.length) || areCatalogsNotInSameLocation;
    setIsCreateInvoiceDisabled(isDisabledButton);
  }, [invoiceSettingsID, groupTitle, catalogRefs, areCatalogsNotInSameLocation]);

  const catalogsRefsInCurrentGroupId = useMemo(() => {
    if (!catalogRefs.length || !invoiceGroups.length || !currentGroupId) {
      return [];
    }

    const currentInvoiceGroup = invoiceGroups.find(
      (invoiceGroup) => invoiceGroup.groupID === currentGroupId
    );

    if (!currentInvoiceGroup || !currentInvoiceGroup.catalogs) {
      return [];
    }

    return currentInvoiceGroup.catalogs.reduce(
      (acc, catalog) => {
        if (catalogRefs.includes(catalog.catalogRef)) {
          acc.push(catalog);
        }

        return acc;
      }, []
    );
  }, [catalogRefs, currentGroupId, invoiceGroups]);

  useEffect(() => {
    if (catalogsRefsInCurrentGroupId.length <= 1) {
      setAreCatalogsNotInSameLocation(false);

      return;
    }

    const catalogsRefsInCurrentGroupIdSize = catalogsRefsInCurrentGroupId.length;
    const catalogAddressId = catalogsRefsInCurrentGroupId[0].addressID;
    let isDifferentLocation = false;

    for (let i = 1; i < catalogsRefsInCurrentGroupIdSize; i++) {
      const currentCatalogAddressId = catalogsRefsInCurrentGroupId[i].addressID;

      if (currentCatalogAddressId !== catalogAddressId) {
        isDifferentLocation = true;

        break;
      }
    }

    if (isDifferentLocation !== areCatalogsNotInSameLocation) {
      setAreCatalogsNotInSameLocation(isDifferentLocation);
    }
  }, [catalogsRefsInCurrentGroupId]);

  useEffect(() => {
    toggleCatalogsNotInSameLocationBanner(areCatalogsNotInSameLocation);
  }, [areCatalogsNotInSameLocation]);

  const getCatalogsRefsByGroupId = useCallback((groupId) => {
    if (!groupId) {
      return [];
    }

    const invoiceGroupsSize = invoiceGroups.length;

    for (let i = 0; i < invoiceGroupsSize; i++) {
      if (invoiceGroups[i].groupID === groupId) {
        const { catalogs = [] } = invoiceGroups[i];

        return catalogs.map((catalog) => catalog.catalogRef);
      }
    }

    return [];
  }, [invoiceGroups]);

  const isCatalogRefSaved = useCallback(
    (currentCatalogRef) => catalogRefs.includes(currentCatalogRef),
    [catalogRefs]
  );

  const hasGroupCatalogRefSaved = useCallback(
    (groupId) => {
      if (!catalogRefs.length) {
        return false;
      }

      const currentCatalogsRefs = getCatalogsRefsByGroupId(groupId);

      return currentCatalogsRefs.length === catalogRefs.length && groupId === currentGroupId;
    }, [catalogRefs, getCatalogsRefsByGroupId]
  );

  const disabledCheckboxNotInGroupId = useCallback((groupId) => (currentGroupId
    ? groupId !== currentGroupId && !!catalogRefs.length
    : false),
  [catalogRefs, currentGroupId]);

  const mapCatalogRefsToObject = (catalogsRefs = []) => {
    if (!Array.isArray(catalogsRefs)) {
      return {};
    }

    return catalogsRefs.reduce((acc, current) => {
      acc[current] = current;

      return acc;
    }, {});
  };

  const handleTemplateSection = (id, named) => {
    setInvoiceSettingsID(id);
    setTemplatesLabel(named);
  };

  const limitGroupNameLength = (name) => {
    let displayName = name;
    if (name.length > 50) {
      displayName = `${name.substring(0, 50)}...`;
    }
    setGroupTitle(displayName);
    setGroupNameLength(Math.min(displayName.length, 50));
  };

  const updateCatalogRefs = (
    isNewAddition = true,
    catalogsRefs = [], catalogRefsGroupName,
    groupId
  ) => {
    if (isNewAddition) {
      setCatalogRefs((prev) => [
        ...new Set([
          ...(groupId === currentGroupId ? prev : []),
          ...catalogsRefs
        ])
      ]);
    } else {
      const catalogsRefsObj = mapCatalogRefsToObject(catalogsRefs);

      setCatalogRefs((prev) => prev.filter(
        current => typeof catalogsRefsObj[current] === 'undefined'
      ));
    }

    limitGroupNameLength(catalogRefsGroupName);

    if (currentGroupId !== groupId) {
      setCurrentGroupId(groupId);
    }
  };

  const handleOnCreateInvoicesClick = () => {
    setIsCreateInvoiceDisabled(true);
    setIsOpenCreatingInvoiceModal(true);
    exec({
      data: {
        catalogRefs,
        groupTitle,
        invoiceSettingsID,
      }
    });
  };

  if (isIdle || isLoading) {
    return <ReactLoading className="loading-spinner" type="spin" color="black" />;
  }

  return (
    <>
      { isShowInvoiceCreationError && <Alert color="danger">There was an error while creating invoice.</Alert> }
      <Row>
        <Col xs="12" className={`${blockEl}__header-actions-group`}>
          <CreatingInvoiceModal isOpen={isOpenCreatingInvoiceModal} />
          <div className="d-inline-block">
            <div className="font-weight-bold">
              Invoice Template
            </div>
            <div>
              <UncontrolledDropdown className="w-100">
                <DropdownToggle
                  caret
                  color="white"
                  className="text-truncate"
                  style={{
                    color: isTemplatesLabelPlaceholderVisible ? '#bbb' : 'inherit'
                  }}
                  disabled={!catalogRefs.length}
                >
                  {templateslabel} &nbsp;
                  <FontAwesomeIcon icon="angle-down" />
                </DropdownToggle>
                <DropdownMenu className="full-width-dropdown" disabled={!catalogRefs.length}>
                  {invoiceTemplates.map(
                    template => (
                      <DropdownItem
                        key={template.id}
                        id={template.id}
                        className="w-100 text-clamp-2"
                        onClick={() => handleTemplateSection(template.id, template.name)}
                      >
                        {template.name}
                      </DropdownItem>
                    )
                  )}
                  <DropdownItem divider />
                  <DropdownItem
                    tag="a"
                    href="/ar/invoices/invoiceTemplates"
                  >
                    <span className="btn btn-link">
                      <FontAwesomeIcon icon="plus" /> Create new template
                    </span>
                  </DropdownItem>
                </DropdownMenu>
              </UncontrolledDropdown>
            </div>
          </div>
          <div className={classNames(
            'd-inline-block ml-4 position-relative',
            `${blockEl}__group-name`
          )}
          >
            <div className="font-weight-bold">
              Invoice Group Name
            </div>
            <div>
              <Input
                disabled={!catalogRefs.length}
                id="invoiceGroupName"
                name="invoiceGroupName"
                onChange={e => limitGroupNameLength(e.target.value)}
                type="text"
                value={groupTitle}
              />
              <div className="position-absolute small-important w-100 text-right">
                {groupNameLength}/50 characters
              </div>
            </div>
          </div>
          <div className="d-inline-block ml-4">
            <div>
              <button
                className={classNames(
                  'btn btn-primary',
                  isCreateInvoiceDisabled && 'cursor-not-allowed'
                )}
                type="button"
                aria-label="Create Invoice"
                disabled={isCreateInvoiceDisabled}
                onClick={handleOnCreateInvoicesClick}
              >
                <FontAwesomeIcon icon="plus" className="color-white-bg" /> Create New Invoice
              </button>
            </div>
          </div>
        </Col>
      </Row>
      {invoiceGroups.length
        ? invoiceGroups.map(
          group => (
            <Fragment key={group.groupID}>
              <Row className="mt-4">
                <Col xs="12">
                  <div className="auction-container w-100 pl-3 font-weight-bold">
                    {group.groupName}
                  </div>
                </Col>
              </Row>
              <Row>
                <Col xs="12">
                  <div className="border">
                    <div className={classNames(
                      'd-inline-block ml-3',
                      `${blockEl}__checkbox`
                    )}
                    >
                      <CatalogGroupCheckbox
                        disabled={disabledCheckboxNotInGroupId(group.groupID)}
                        checked={hasGroupCatalogRefSaved(group.groupID)}
                        onChange={(e) => updateCatalogRefs(
                          e.target.checked,
                          getCatalogsRefsByGroupId(group.groupID),
                          group.groupName,
                          group.groupID
                        )}
                      />
                    </div>
                    <div className="w-25 font-weight-bold pl-3 py-2 d-inline-block">
                      Auction Date
                    </div>
                    <div className="font-weight-bold pl-3 py-2 d-inline-block">
                      Sale Title
                    </div>
                  </div>
                </Col>

                {group.catalogs && group.catalogs.length
                  ? group.catalogs.map(
                    catalog => (
                      <Col key={catalog.catalogRef} xs="12">
                        <div className="border">
                          <div className={classNames(
                            'd-inline-block ml-3',
                            `${blockEl}__checkbox`
                          )}
                          >
                            <Input
                              type="checkbox"
                              checked={isCatalogRefSaved(catalog.catalogRef)}
                              onChange={(e) => {
                                updateCatalogRefs(e.target.checked, [catalog.catalogRef], group.groupName, group.groupID);
                              }}
                            />
                          </div>
                          <div className="w-25 pl-3 py-2 d-inline-block">
                            {catalog.catalogDateFull}
                          </div>
                          <div className="pl-3 py-2 d-inline-block">
                            {catalog.catalogTitle}
                          </div>
                        </div>
                      </Col>
                    )
                  )
                  : null}
              </Row>
            </Fragment>
          )
        )
        : null }

      <Row className="my-5">
        <Col xs={5} className="text-right">
          {startRowIndex > 0 && (
            <a href={`${imagePath}/invoices/?page=${currentPage - 1}`}>
              &lt; Previous
            </a>
          )}
        </Col>
        <Col xs={2} className="text-center">
          {startRowIndex + 1}-{Math.min(endRowIndex, totalGroups)} of {totalGroups}
        </Col>
        <Col xs={5}>
          {totalGroups > endRowIndex && (
          <a href={`${imagePath}/invoices/?page=${parseInt(currentPage, 10) + 1}`}>
            Next &gt;
          </a>
          )}
        </Col>
      </Row>
    </>
  );
};

UninvoicedSessions.propTypes = {
  toggleCatalogsNotInSameLocationBanner: PropTypes.func.isRequired
};

export default UninvoicedSessions;
